import { Table } from "@mantine/core";

export type Cell = string | number | boolean | null;

interface Props {
  columns: string[];
  /** Positional, parallel to `columns` — the shape the API returns. */
  rows: Cell[][];
  formatValue?: (v: number) => string;
}

/**
 * Every table in the app: the twin of a chart, and the table widget itself.
 *
 * A chart's twin is not optional. A tooltip must never be the only way to read
 * a value, and this is the WCAG-clean equivalent for anyone who cannot use the
 * plot — which is also what discharges the light-mode contrast warning on the
 * categorical palette (see `tokens.ts`).
 *
 * Alignment follows the value, not the column: numbers go right with tabular
 * figures so digits line up down the column, text goes left.
 */
export function DataTable({ columns, rows, formatValue }: Props) {
  const fmt = formatValue ?? ((v: number) => v.toLocaleString());

  return (
    <Table.ScrollContainer minWidth={320}>
      <Table striped highlightOnHover withTableBorder={false}>
        <Table.Thead>
          <Table.Tr>
            {columns.map((column, i) => (
              <Table.Th
                key={column}
                style={{ textAlign: isNumeric(rows, i) ? "right" : "left" }}
              >
                {column}
              </Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {rows.map((row, r) => (
            <Table.Tr key={r}>
              {row.map((cell, c) => (
                <Table.Td
                  key={c}
                  style={
                    typeof cell === "number"
                      ? { textAlign: "right", fontVariantNumeric: "tabular-nums" }
                      : undefined
                  }
                >
                  {render(cell, fmt)}
                </Table.Td>
              ))}
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

/** Header alignment matches its column's values, judged from the first row. */
function isNumeric(rows: Cell[][], column: number): boolean {
  return typeof rows[0]?.[column] === "number";
}

function render(cell: Cell, fmt: (v: number) => string): string {
  if (cell === null) return "—";
  if (typeof cell === "number") return fmt(cell);
  if (typeof cell === "boolean") return cell ? "Yes" : "No";
  return cell;
}
